import type { ConstanciaCreadaDTO, ItemSalidaLocal } from '@ausentia/shared';
import { bd, type AdjuntoLocal } from './db.js';
import { api, ErrorApi } from '../api/cliente.js';
import { estaOnline, registrarIntentoFallido, suscribirRed } from './red.js';
import { base64DeDataUrl } from './cripto.js';

/**
 * Outbox del dispositivo (patrón outbox + worker, sección 5 de la spec).
 * Todo control —online u offline— pasa por acá: si hay red, se sincroniza al
 * instante; si no, queda ESPERANDO_RED y un worker reintenta con backoff. La
 * `idempotencyKey` del payload garantiza que un reintento no duplique la
 * constancia en el servidor (HU-10, decisión del contrato del Anexo B).
 */

type Oyente = () => void;
const oyentes = new Set<Oyente>();
function notificar() {
  for (const o of oyentes) o();
}
export function suscribirOutbox(o: Oyente): () => void {
  oyentes.add(o);
  return () => oyentes.delete(o);
}

export interface DatosEncolar {
  ordenId: string;
  idempotencyKey: string;
  tipo: ItemSalidaLocal['tipo'];
  payload: Record<string, unknown>;
  adjuntosLocales: string[];
  resumen: string;
  contingencia: boolean;
}

export async function encolarControl(datos: DatosEncolar): Promise<number> {
  const id = await bd.outbox.add({
    ...datos,
    estado: 'ESPERANDO_RED',
    creadoEn: new Date().toISOString(),
  });
  notificar();
  return id as number;
}

export async function itemsOutbox(): Promise<ItemSalidaLocal[]> {
  return (await bd.outbox.orderBy('id').reverse().toArray()) as ItemSalidaLocal[];
}

export async function pendientesOutbox(): Promise<number> {
  return bd.outbox.where('estado').anyOf('ESPERANDO_RED', 'ERROR', 'SUBIENDO').count();
}

async function subirAdjunto(local: AdjuntoLocal): Promise<string> {
  if (local.uploadId) return local.uploadId;
  const r = await api.post<{ uploadId: string }>('/api/adjuntos', {
    tipo: local.tipo,
    contenidoBase64: base64DeDataUrl(local.dataUrl),
    hashSha256: local.hashSha256,
    mime: local.mime,
    capturadaEn: local.capturadaEn,
    geo: local.geo,
  });
  await bd.adjuntos.update(local.id, { uploadId: r.uploadId });
  return r.uploadId;
}

export interface ResultadoSync {
  itemId: number;
  ok: boolean;
  respuesta?: ConstanciaCreadaDTO;
  error?: string;
}

/** Sincroniza un único item (subir adjuntos → registrar control). */
async function sincronizarItem(item: ItemSalidaLocal): Promise<ResultadoSync> {
  const payload = structuredClone(item.payload) as Record<string, unknown> & {
    contingencia?: { adjuntos?: { uploadId: string }[] };
  };

  // Resolver uploadId de cada adjunto local referenciado en el payload.
  const adjuntosPayload = payload.contingencia?.adjuntos ?? [];
  for (const ref of adjuntosPayload) {
    const local = await bd.adjuntos.get(ref.uploadId);
    if (local) ref.uploadId = await subirAdjunto(local);
  }

  const respuesta = await api.post<ConstanciaCreadaDTO>('/api/controles', payload);
  await bd.outbox.update(item.id!, {
    estado: 'SINCRONIZADO',
    constanciaCodigo: respuesta.codigo,
    challengeId: respuesta.challenge?.challengeId,
    error: undefined,
  });
  return { itemId: item.id!, ok: true, respuesta };
}

let sincronizando = false;

/** Procesa todos los pendientes. Si no hay red, registra el intento fallido. */
export async function sincronizar(): Promise<ResultadoSync[]> {
  if (sincronizando) return [];
  if (!estaOnline()) {
    const hay = await pendientesOutbox();
    if (hay) registrarIntentoFallido('Sin conexión: envío diferido');
    return [];
  }
  sincronizando = true;
  const resultados: ResultadoSync[] = [];
  try {
    // Solo ESPERANDO_RED entra al reintento automático. ERROR es un rechazo de
    // negocio (4xx) ya mostrado al médico: antes también se reintentaba acá
    // cada 8s, así que un control duplicado o inválido se reenviaba a la API
    // indefinidamente. Sale de ERROR solo si el médico lo descarta.
    const pendientes = (await bd.outbox
      .where('estado')
      .equals('ESPERANDO_RED')
      .toArray()) as ItemSalidaLocal[];
    for (const item of pendientes) {
      await bd.outbox.update(item.id!, { estado: 'SUBIENDO' });
      notificar();
      try {
        resultados.push(await sincronizarItem(item));
      } catch (err) {
        const esRechazoDeNegocio = err instanceof ErrorApi && err.estado >= 400 && err.estado < 500;
        const mensaje = err instanceof ErrorApi ? `${err.codigo}: ${err.message}` : String(err);
        // Un rechazo de negocio (4xx) no es reintentable: queda en ERROR para
        // que el médico lo vea y decida. Cualquier otra falla (de red, 5xx)
        // vuelve a ESPERANDO_RED para que el próximo tick la reintente sola.
        await bd.outbox.update(item.id!, {
          estado: esRechazoDeNegocio ? 'ERROR' : 'ESPERANDO_RED',
          error: mensaje,
        });
        resultados.push({ itemId: item.id!, ok: false, error: mensaje });
      }
      notificar();
    }
  } finally {
    sincronizando = false;
    notificar();
  }
  return resultados;
}

export async function descartarItem(id: number) {
  await bd.outbox.delete(id);
  notificar();
}

/** Worker: sincroniza al volver la red y cada 8 s si hay pendientes. */
export function iniciarWorkerOutbox(): () => void {
  // Items en estado SUBIENDO al arrancar son huérfanos de una sesión interrumpida.
  void bd.outbox.where('estado').equals('SUBIENDO').modify({ estado: 'ESPERANDO_RED' });

  const desuscribir = suscribirRed((online) => {
    if (online) void sincronizar();
  });
  const timer = setInterval(() => {
    if (estaOnline()) void sincronizar();
  }, 8000);
  return () => {
    desuscribir();
    clearInterval(timer);
  };
}
